import JSZip from 'jszip';
import type { Job } from './types';
import { unique } from './utils';

/**
 * Check if URL has an image extension
 * Fast path for image detection without network requests
 */
export function hasImageExtension(url: string): boolean {
  const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.svg'];
  const urlLower = url.toLowerCase().split('?')[0]; // Remove query params
  return extensions.some((ext) => urlLower.endsWith(ext));
}

/**
 * Check if URL has a ZIP extension
 */
export function hasZipExtension(url: string): boolean {
  const urlLower = url.toLowerCase().split('?')[0]; // Remove query params
  return urlLower.endsWith('.zip');
}

/**
 * Check MIME type via GET request (aborted after headers)
 * Uses GET instead of HEAD because AWS signed URLs only allow the signed method
 * For extensionless URLs that need content-type detection
 */
export async function checkImageMimeType(url: string): Promise<boolean> {
  const controller = new AbortController();

  try {
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Get content-type from headers
    const contentType = response.headers.get('content-type') || '';
    const isImage = contentType.startsWith('image/');

    // Abort the download immediately after getting headers
    controller.abort();

    return isImage;
  } catch (error) {
    // If fetch fails (timeout, network error, abort, etc.), return false
    // Note: AbortError is expected after we abort, so we catch all errors
    return false;
  }
}

/**
 * Check if URL is an image
 * Uses extension first (fast), then MIME type check (slow)
 */
export async function isImageUrl(url: string): Promise<boolean> {
  // Fast path: check extension first
  if (hasImageExtension(url)) {
    return true;
  }

  // Slow path: check MIME type for extensionless URLs
  return await checkImageMimeType(url);
}

/**
 * Extract all image URLs from a job
 * Checks both input_file and output array
 */
export async function extractJobImages(job: Job): Promise<{ input?: string; output: string[] }> {
  const images: { input?: string; output: string[] } = { output: [] };

  // Check input image
  if (job.payload?.input_file && typeof job.payload.input_file === 'string') {
    if (await isImageUrl(job.payload.input_file)) {
      images.input = job.payload.input_file;
    }
  }

  // Check output images
  if (job.result?.result?.output && Array.isArray(job.result.result.output)) {
    const urlsToCheck = job.result.result.output.filter(
      (item: unknown): item is string => typeof item === 'string'
    );

    // Check all URLs in parallel for better performance
    const checks = await Promise.all(
      urlsToCheck.map(async (url: string) => ({
        url,
        isImage: await isImageUrl(url),
      }))
    );

    // De-dupe here, at the single producer, so every output-image consumer is unique by construction.
    images.output = unique(checks.filter((c) => c.isImage).map((c) => c.url));
  }

  return images;
}

/**
 * Extract filename from URL
 * Handles URLs with or without query parameters
 */
export function getFilenameFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    let filename = pathname.substring(pathname.lastIndexOf('/') + 1);

    // Remove query parameters from filename if any leaked through
    filename = filename.split('?')[0];

    return filename || 'image';
  } catch {
    return 'image';
  }
}

/**
 * Extract images from a ZIP file
 * Downloads ZIP, extracts it, and returns blob URLs for images in root folder only
 *
 * @param zipUrl - URL to ZIP file
 * @returns Array of objects with { filename, blobUrl }
 */
export async function extractImagesFromZip(
  zipUrl: string
): Promise<Array<{ filename: string; blobUrl: string }>> {
  try {
    // Download ZIP file
    const response = await fetch(zipUrl);
    if (!response.ok) {
      throw new Error(`Failed to download ZIP: ${response.status}`);
    }

    const arrayBuffer = await response.arrayBuffer();

    // Extract ZIP
    const zip = new JSZip();
    const zipContents = await zip.loadAsync(arrayBuffer);

    const images: Array<{ filename: string; blobUrl: string }> = [];

    // Look for images in root only (no subfolders)
    for (const filename of Object.keys(zipContents.files)) {
      const file = zipContents.files[filename];

      // Skip directories and files in subfolders
      if (file.dir || filename.includes('/')) {
        continue;
      }

      // Check if it's an image by extension
      if (hasImageExtension(filename)) {
        // Extract file as blob
        const blob = await file.async('blob');

        // Create blob URL
        const blobUrl = URL.createObjectURL(blob);

        images.push({
          filename,
          blobUrl,
        });
      }
    }

    return images;
  } catch (error) {
    console.error('Failed to extract images from ZIP:', error);
    throw error;
  }
}
